/** @format */

import { createAsyncThunk } from "@reduxjs/toolkit";
import {
	// Service imports
	fetchPlatformFeeRevenue,
	fetchStripeAccountBalance,
	fetchStripeActivitySummary,
	// Type imports
	FeeRevenueQueryParams,
	StripeActivityQueryParams,
} from "../services/revenueService";

// ==================== Response Types ====================

export interface LateFeeRecord {
	id: string;
	timestamp: string;
	dateFormatted: string;
	amountCents: number;
	amountDollars: string;
	memberId: string;
	memberName: string;
	memberEmail: string;
	circleName: string;
	circleId: string;
	cycleId: string;
	missedDatesCount: number;
	missedDates: string;
	feePercentage: string;
	paymentIntentId: string;
	description: string;
}

export interface PayoutFeeRecord {
	id: string;
	timestamp: string;
	dateFormatted: string;
	feeCents: number;
	feeDollars: string;
	grossPayoutCents: number;
	grossPayoutDollars: string;
	netPayoutCents: number;
	netPayoutDollars: string;
	memberId: string;
	memberName: string;
	memberEmail: string;
	circleName: string;
	circleId: string;
	cycleId: string;
	feePercentage: string;
	stripeTransferId: string;
	description: string;
}

export interface StripeBalanceInfo {
	available: number;
	pending: number;
	total: number;
	availableFormatted: string;
	pendingFormatted: string;
	totalFormatted: string;
	currency: string;
}

export interface StripeActivitySummary {
	charges: {
		count: number;
		totalCents: number;
		totalFormatted: string;
		feesCents: number;
		feesFormatted: string;
	};
	transfers: {
		count: number;
		totalCents: number;
		totalFormatted: string;
	};
	netRetained: {
		cents: number;
		formatted: string;
	};
}

export interface FeeRevenueByCircle {
	circleId: string;
	circleName: string;
	lateFeeCents: number;
	payoutFeeCents: number;
	totalCents: number;
}

export interface FeeRevenueByMember {
	memberId: string;
	memberName: string;
	memberEmail: string;
	lateFeeCents: number;
	payoutFeeCents: number;
	totalCents: number;
}

export interface FeeRevenueByMonth {
	monthKey: string;
	monthLabel: string;
	lateFeeCents: number;
	payoutFeeCents: number;
	totalCents: number;
}

export interface PlatformFeeRevenueResponse {
	filter: {
		mode: string;
		label: string;
		startDate?: string;
		endDate?: string;
	};
	summary: {
		lateFees: {
			totalCents: number;
			totalDollars: string;
			count: number;
		};
		payoutFees: {
			totalCents: number;
			totalDollars: string;
			count: number;
		};
		grandTotal: {
			cents: number;
			dollars: string;
		};
	};
	stripeBalance: StripeBalanceInfo | null;
	stripeActivity: StripeActivitySummary | null;
	byMonth: FeeRevenueByMonth[];
	byCircle: FeeRevenueByCircle[];
	byMember: FeeRevenueByMember[];
	lateFeeRecords: LateFeeRecord[];
	payoutFeeRecords: PayoutFeeRecord[];
	generatedAt: string;
}

// ==================== Async Thunk Actions ====================

/**
 * Get complete platform fee revenue report
 */
export const getPlatformFeeRevenue = createAsyncThunk(
	"revenue/getPlatformFeeRevenue",
	async (params: FeeRevenueQueryParams | undefined, { rejectWithValue }) => {
		try {
			const response = await fetchPlatformFeeRevenue(params);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.message || "Failed to fetch platform fee revenue",
			);
		}
	},
);

/**
 * Get current Stripe account balance
 */
export const getStripeAccountBalance = createAsyncThunk(
	"revenue/getStripeAccountBalance",
	async (_, { rejectWithValue }) => {
		try {
			const response = await fetchStripeAccountBalance();
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.message || "Failed to fetch Stripe account balance",
			);
		}
	},
);

/**
 * Get Stripe activity summary
 */
export const getStripeActivitySummary = createAsyncThunk(
	"revenue/getStripeActivitySummary",
	async (
		params: StripeActivityQueryParams | undefined,
		{ rejectWithValue },
	) => {
		try {
			const response = await fetchStripeActivitySummary(params);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.message || "Failed to fetch Stripe activity summary",
			);
		}
	},
);
